import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, X, Plus, User, FileText, Calendar, Loader2 } from 'lucide-react';
import { Task } from '../../lib/types';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { useApp } from '../AppProvider';
import { resolveTaskLeadId } from '../../lib/utils/leadStageQuickChange';
import { useLeadStageQuickChange } from './task-completion/useLeadStageQuickChange';
import { LeadStageQuickChange } from './task-completion/LeadStageQuickChange';

interface TaskCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (taskId: string, outcome: string, createNew?: boolean) => Promise<void>;
  task: Task | null;
}

/**
 * Modale di completamento task commerciale in stile pannello: riepilogo a
 * righe di proprietà, textarea slim, footer Annulla/Salva/Salva e nuova.
 */
export function TaskCompletionModal({ isOpen, onClose, onComplete, task }: TaskCompletionModalProps) {
  const [outcome, setOutcome] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingNew, setIsSubmittingNew] = useState(false);
  const { settings } = useSettingsStore();
  const outcomeRequired = settings?.activity_outcome_required !== false;
  const { toast } = useApp();
  // Cambio fase del Lead nello stesso modale (segnalazione cac9836a): niente nota a parte.
  const leadStage = useLeadStageQuickChange(resolveTaskLeadId(task as any), isOpen);

  // Esito vuoto a ogni apertura / cambio task (segnalazione 4188603f): il modale resta
  // montato fra un'apertura e l'altra, e con "Annulla" il testo scritto per la task
  // precedente ricompariva sulla successiva.
  useEffect(() => {
    if (isOpen) setOutcome('');
  }, [isOpen, task?.id]);

  // Check if task has context for creating new activities (deal_id or client_id)
  const hasContextForNewActivity = Boolean(task?.deal_id || task?.client_id || task?.lead_id);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [isOpen, onClose]);

  // Animazione apertura/chiusura: resta montato durante l'uscita, fade del
  // backdrop + fade/scale del pannello (doppio rAF per far partire la transizione).
  const [shouldRender, setShouldRender] = useState(false);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      const raf = requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
      return () => cancelAnimationFrame(raf);
    }
    setVisible(false);
    const t = setTimeout(() => setShouldRender(false), 180);
    return () => clearTimeout(t);
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent | React.MouseEvent, createNew: boolean = false) => {
    e.preventDefault();
    if (!task || (outcomeRequired && !outcome.trim())) return;
    if (leadStage.validationError) {
      toast.error(leadStage.validationError);
      return;
    }

    if (createNew) {
      setIsSubmittingNew(true);
    } else {
      setIsSubmitting(true);
    }

    try {
      await onComplete(task.id, outcome, createNew);
      try {
        if (await leadStage.commit()) toast.success('Fase del lead aggiornata');
      } catch (stageError) {
        console.error('Failed to update lead stage:', stageError);
        toast.error('Task completata, ma la fase del lead non e\' stata aggiornata');
      }
      setOutcome('');
      onClose();
    } catch (error) {
      console.error('Failed to complete task:', error);
    } finally {
      setIsSubmitting(false);
      setIsSubmittingNew(false);
    }
  };

  if (!shouldRender) return null;

  const busy = isSubmitting || isSubmittingNew;
  const disabled = busy || (outcomeRequired && !outcome.trim()) || !!leadStage.validationError;

  const clientName = task?.client_name || task?.clients?.business_name;
  const partnerName = (task as any)?.partner_name;

  const rowCls = 'flex min-h-[26px] items-center gap-2 text-[12.5px]';
  const rowIcoCls = 'h-3 w-3 flex-shrink-0 text-[#787774] dark:text-gray-500';
  const rowLabelCls = 'w-20 flex-shrink-0 text-[#787774] dark:text-gray-400';
  const rowValCls = 'min-w-0 flex-1 truncate text-[#37352F] dark:text-white';

  return createPortal(
    <div
      className={`fixed inset-0 z-[21000] flex items-center justify-center bg-black/10 p-4 transition-opacity duration-150 ${visible ? 'opacity-100' : 'opacity-0'}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div role="dialog" aria-modal="true" aria-label="Task completata" className={`flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-xl border border-[rgba(55,53,47,0.09)] bg-white shadow-xl transition-all duration-150 ease-out dark:border-white/10 dark:bg-[#202020] ${visible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-1">
          <span className="text-[11px] font-semibold uppercase tracking-widest bg-gradient-to-r from-[#E91E8C] to-[#F97316] bg-clip-text text-transparent">
            Task completata
          </span>
          <button
            type="button" onClick={onClose} aria-label="Chiudi"
            className="rounded p-1 text-[#787774] hover:bg-[rgba(55,53,47,0.08)] dark:text-gray-400 dark:hover:bg-white/[0.06]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="flex-grow overflow-auto px-5 pb-4">
          {/* Riepilogo task a righe di proprietà */}
          {task && (
            <div className="mt-1">
              <div className="flex items-start gap-2">
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#1FA855]" strokeWidth={1.5} />
                <h3 className="text-[15px] font-semibold leading-snug text-[#37352F] dark:text-white">
                  {task.title}
                </h3>
              </div>
              <div className="mt-2 space-y-0.5 border-l-2 border-[rgba(55,53,47,0.09)] pl-3 dark:border-white/10">
                {clientName ? (
                  <div className={rowCls}>
                    <User className={rowIcoCls} strokeWidth={1.5} />
                    <span className={rowLabelCls}>Cliente</span>
                    <span className={rowValCls}>{clientName}</span>
                  </div>
                ) : partnerName ? (
                  <div className={rowCls}>
                    <User className={rowIcoCls} strokeWidth={1.5} />
                    <span className={rowLabelCls}>Partner</span>
                    <span className={rowValCls}>{partnerName}</span>
                  </div>
                ) : null}
                {task.deal_title && (
                  <div className={rowCls}>
                    <FileText className={rowIcoCls} strokeWidth={1.5} />
                    <span className={rowLabelCls}>Offerta</span>
                    <span className={rowValCls}>
                      {task.deal_title}
                      {task.deal_amount != null && (
                        <span className="ml-1 text-[#787774] dark:text-gray-400">
                          · {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(task.deal_amount)}
                        </span>
                      )}
                    </span>
                  </div>
                )}
                {task.due_date && (
                  <div className={rowCls}>
                    <Calendar className={rowIcoCls} strokeWidth={1.5} />
                    <span className={rowLabelCls}>Scadenza</span>
                    <span className={rowValCls}>
                      {format(parseISO(task.due_date), 'dd/MM/yyyy', { locale: it })}
                      <span className="ml-1 text-[#787774] dark:text-gray-400">· {format(parseISO(task.due_date), 'HH:mm')}</span>
                    </span>
                  </div>
                )}
                {task.description && (
                  <div
                    className="line-clamp-2 pt-1 text-[12px] text-[#787774] dark:text-gray-400"
                    dangerouslySetInnerHTML={{ __html: task.description }}
                  />
                )}
              </div>
            </div>
          )}

          <form onSubmit={(e) => handleSubmit(e)} className="mt-4">
            <label htmlFor="outcome" className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-[#787774] dark:text-gray-500">
              Esito dell'attività{outcomeRequired && <span className="ml-0.5 text-[#EB5757]">*</span>}
            </label>
            <textarea
              id="outcome"
              rows={4}
              autoFocus
              className="block w-full resize-none rounded-md border border-[rgba(55,53,47,0.16)] bg-transparent px-3 py-2 text-[13px] text-[#37352F] outline-none transition-colors placeholder-[rgba(55,53,47,0.45)] focus:border-[#2383E2] dark:border-white/15 dark:text-white dark:placeholder-gray-500"
              placeholder="Descrivi il risultato dell'attività…"
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              required={outcomeRequired}
            />
            <LeadStageQuickChange state={leadStage} />
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-[rgba(55,53,47,0.09)] bg-white px-4 py-3 dark:border-white/10 dark:bg-[#202020]">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded px-3 py-1.5 text-[12.5px] font-medium text-[#37352F] hover:bg-[rgba(55,53,47,0.08)] disabled:opacity-50 dark:text-white dark:hover:bg-white/[0.06]"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={(e) => handleSubmit(e, true)}
            disabled={disabled}
            title={hasContextForNewActivity
              ? 'Salva questa attività e crea una nuova attività correlata'
              : 'Salva e vai a selezionare dove creare la prossima attività'}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded border border-[rgba(55,53,47,0.16)] px-3 py-1.5 text-[12.5px] font-medium text-[#37352F] hover:bg-[rgba(55,53,47,0.06)] disabled:opacity-50 dark:border-white/20 dark:text-white dark:hover:bg-white/[0.06]"
          >
            {isSubmittingNew
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
              : <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />}
            Salva e nuova
          </button>
          <button
            type="submit"
            onClick={(e) => handleSubmit(e)}
            disabled={disabled}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded bg-[#37352F] px-3.5 py-1.5 text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-50 dark:bg-white dark:text-[#191919]"
          >
            {isSubmitting
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
              : <Check className="h-3.5 w-3.5" strokeWidth={1.5} />}
            Salva
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
